// screens/Clark/answer/ClarkAnswerV2.jsx — RUN 5 · PARTS 1, 2 and 4. The prose answer's renderer.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 WHY THIS IS A SECOND COMPONENT AND NOT A BRANCH INSIDE ResearchAnswer.
//
// ResearchAnswer renders GATE-VERIFIED SENTENCES: every `<ProseCite>` it paints carries
// `status: "confident"` and the label "Verified against the CiteClerk corpus", which
// clarkAnswerShape.js stamps on a cite only after the sentence carrying it passed Steel's
// verification gate. The v2 pipeline has no sentence gate — its prose is model output that has been
// through AUTHORITY RESOLUTION, which is a different and much weaker claim.
//
// A branch inside that file would have put two claim-strengths one `if` apart, sharing a stylesheet
// and a set of sub-components whose names ("ProseCite", "VerificationTrace") assert the stronger
// one. The previous session declined to map these shapes onto each other for exactly this reason
// and was right to. So: separate component, separate vocabulary, and NOTHING here renders a
// per-sentence verification claim, because there is none to render.
//
// 🔴 RUN VB — AND AS OF THIS RUN, NOTHING IS CLAIMED UNDER THE ANSWER AT ALL.
// A ledger of counts stood at the foot of this component (`HowWeKnow`, RUN D · PART 2). Adam, on
// production: *"That all looks bad. Remove it all. The user does NOT need to know all that."* ·
// *"No content should be under the answer."* It is gone, with the two See-also blocks beside it —
// see the tombstone below for what was deleted, what was kept, and what none of it touched.
//
// ⭐ WHAT SURVIVES IS THE SIGNAL, NOT THE EXPLANATION. An authority the corpus could not confirm is
// still MARKED IN THE PROSE, on the sentence that rests on it (`Prose` below, `cc-v2-unresolved` /
// `cc-v2-unchecked`). The reader lost a paragraph about the checks; they did not lose the ability
// to see which sentence is standing on one that failed. That distinction is the whole of this run.

import { useState, useEffect, useRef, useMemo } from "react";
/* RUN TA — the reveal. The pacing is pure and lives in `answer/revealPace.mjs` (imported from
   there rather than through `answerApi.js` so this component does not pull the whole research
   transport in to animate a paragraph); the WHY is in `answerApi.js` beside its re-export. */
import { revealTotal, revealDurationMs, revealSegments } from "./revealPace.mjs";
import { prefersReducedMotion } from "./parts.jsx";
import { openInResearchHref } from "../handoff.js";
import { hasOpenableAuthorities } from "./authorityLink.mjs";
import { scopeStampLabel } from "../jurisdictions.js";
/* 🔴 THE SAME CARD THE RAIL RENDERS, NOT A SECOND ONE. `answer/AuthorityCard.jsx`'s header is
   explicit that a hand-built second card is how the ge5 defect recurs — two implementations of one
   idea, free to disagree. A first cut of `UnverifiedAuthorityCard` below wrote `.cc-auth` markup by
   hand and would have been exactly that, plus a stylesheet dependency this file does not declare
   (the shared card imports its own CSS; hand-written markup would have inherited it only because
   the rail happened to be mounted on the same page). */
// 🔴 RUN SH · SH-2 — `toParagraphs` WAS MODULE-PRIVATE HERE AND A SECOND COPY WAS MODULE-PRIVATE
// IN apps/steel. Both headers said so and neither run owned both apps. It is one module now.
// Measured before the swap: both implementations, sliced out of the two files on disk, over 174
// archived and adversarial inputs — 174 of 174 identical (apps/main/ops/run-sh/out/agree-before.json).
import { toParagraphs, dropUnsupportedSentences } from "@citeclerk/ui/answerParagraphs";
/* 🔴 RUN TA (steel) — THE DECLINE'S WORDS LEAVE THIS FILE. They are now `@citeclerk/ui/declineCopy`
   for the reason `howWeKnow` is: RESEARCH RENDERED ZERO CHARACTERS OF DECLINE off the same
   envelope this component renders correctly, and the fix is one module both surfaces read, not a
   second renderer. ⚠️ Not to be confused with the OTHER RUN TA (this app's, the reveal below). */
import { declineOf, searchedClause, disclaimedClause } from "@citeclerk/ui/declineCopy";
import SharedAuthorityCard from "@citeclerk/ui/AuthorityCard";
// RUN RM — the phone's authorities control. Same file as the rail's own `RailItem`, deliberately:
// the two share a card, so neither can drift from the other. See its header for the measurement.
import { MobileAuthorities } from "./AuthorityCard.jsx";
// 🔴 RUN CX — THE SAME MAPPER THE RAIL USES. The phone must not build its own see-also rows: the
// pill vocabulary, the `citeSource` default and the two-array merge all live in `railPayload`, and
// a second copy here is how the two widths would start describing one row differently.
import railPayload from "./railPayload.js";
// RUN OR — the sign-up gate on the Research link. See its header for the measured hole it closes.
import { blockResearchIfSignedOut, isSignedOut } from "../researchGate.js";
import "./answer.css";
import "./answerV2.css";

/**
 * ═══ 🔴 RUN VB — THE BADGE, THEN ITS SUCCESSOR. BOTH GONE, AND FOR THE SAME REASON. ═══════════
 *
 * `VerifiedBadge` stood here until RUN D · PART 2 removed it by measurement (RUN C's frozen
 * 60-question census, `ops/runc-census-v2/RUNC_REPORT.md` §2): 12 of 57 answers went green, 0 of
 * those 12 showed a checked quotation, all 12 were green over ZERO quotations. The badge was never
 * lying — `allSupported` is true over an empty set — but it could only be EARNED by an answer that
 * quoted nothing, so green tracked ABSENCE OF QUOTATION, the opposite of the advertisement.
 *
 * `HowWeKnow` replaced it: the same counts in words, ending in the sentence saying what none of
 * them establishes. Rendered, on RQ04, it read — measured, verbatim, at 267 characters:
 *
 *     "How we know: 5 authorities resolved to a document in our corpus · 2 named authorities could
 *      not be found, and are marked in the text · 0 of 1 quotation checked against the source it
 *      comes from · 1 we could not check None of this is a check that the answer is correct."
 *
 * 🔴 ADAM, ON PRODUCTION, 2026-08-25: *"That all looks bad. Remove it all. The user does NOT need
 * to know all that."* And the closing clause is why, in one line: it undermines every clause before
 * it. Machine vocabulary — "resolved", "corpus", counts over denominators — on a reading surface,
 * concluding that none of it is a check on the thing the reader came for.
 *
 * ⚠️ AND IT CARRIED A MISSING SPACE — `check None of this`, above; Adam's own screenshot caught the
 * same fault one clause over (`directly None`). **The spacing was NOT fixed.** A string nobody
 * reads is what a missing space looks like from the outside, and repairing the typography of a
 * sentence that is being deleted spends the work on the wrong side of the decision. The bug is
 * recorded here because it is evidence FOR the deletion, not a loose end left behind by it.
 *
 * 🔴 NOTHING ABOUT THE CHECK TURNED OFF, AND NOTHING REPLACES THE LINE. `toV2Envelope` still
 * computes every `badge.*` field and the `answer.quotes[]` ledger and they are still on the wire —
 * RUN VC needs them, and `badge.beyondCap` is still read below, inside `Show Authorities`, where a
 * reader has ASKED. `BADGE_LABEL` / `BADGE_TOOLTIP` stay in `apps/steel/server/lib/clarkAnswerV2.js`
 * with the pinned final clause and its test, UNRENDERED. `@citeclerk/ui/howWeKnow` still owns the
 * wording and Research still prints it. This run removed a RENDER, not a computation — and STEP 1
 * proves that by byte-comparing the envelope the component is handed, before and after.
 */
/**
 * The prose. Segments, never a regex over model output on the client.
 *
 * 🔴 AN UNRESOLVED AUTHORITY IS MARKED WHERE IT WAS SAID, AND THE NAME IS KEPT.
 * The temptation is to delete it. That would leave the sentence reading as though it cited nothing,
 * and a lawyer skimming would take the surrounding claim at face value. The name stays, visibly
 * marked, so the reader can see both what was asserted and that we could not confirm it.
 */
// 🔴 RUN C — THIS COMPONENT KNEW ONE MARKER OF THREE, AND THE TWO IT DID NOT KNOW FAILED SILENTLY.
//
// `toSegments` has emitted `{type:'unsupported', index}` since RUN 9 — a segment with NO `text`
// field — and this function's fallback branch is `<span>{s.text}</span>`. React renders `undefined`
// as nothing, so on Clark a withheld quotation was **excised without a trace**: the sentence came
// back ungrammatical with no sign that anything had been removed. That is precisely the
// silent-omission failure the marker was invented to prevent, arriving through the renderer instead
// of through the remedy — and it could not be seen from the server, where the marker was correct.
// Research's `AnswerV2.jsx` handled it; the two surfaces disagreed and nothing said so.
//
// The default branch now renders `s.text ?? ""` explicitly, so a future fourth segment type is a
// visible no-op rather than a hole, and every type this file knows about is listed.
/* ═══ 🔴 RUN RA — THE PARAGRAPH BREAK THE MODEL WROTE, AND THE RENDERER THAT ATE IT ═══════════
 *
 * MEASURED, on `ops/run-ra/_probe-collapse.mjs` against RUN PQ's archived Clark turn R1:
 *
 *     answer.prose               10 newlines, 5 paragraph breaks
 *     prose element .textContent 10 newlines   ← the data reaches the DOM intact
 *     prose element .innerText    0 newlines   ← and every one of them is invisible
 *     block children              0            ← one <span>, `white-space: normal`, 128 lines
 *
 * `.cc-ans-prose` never set `white-space`, and this function emitted one inline `<span>` per
 * segment into a single flow. A `\n` in an inline flow under `white-space: normal` IS a space.
 * So the structure was not missing — it was DELIVERED AND DISCARDED, which is the harder defect to
 * see, because nothing anywhere is empty and no test that reads `textContent` can fail.
 *
 * ⭐ AND THIS FIX IS NOT SUFFICIENT ON ITS OWN — SAID HERE SO NOBODY READS IT AS THE WHOLE ANSWER.
 * Of 36 archived prose strings (`prose` + `withheldProse` across every envelope RUN PQ captured),
 * 34 carry NO newline at all: 2 of 19 on the Clark route, 0 of 17 on the research route.
 * (An earlier draft of this comment said "29 of 31" — it had dropped the five `withheldProse`
 * strings and three clark-supp turns from its own denominator. Recounted; the conclusion is
 * unchanged and slightly stronger.) The model usually emits one unbroken block, and no renderer can break prose
 * that arrives without structure. That half is COMPOSITION — `clarkPipeline.js`'s `SYSTEM` says
 * nothing about paragraphs and `ANSWER_SCHEMA` types `answer` as a bare string — and it belongs to
 * steel. Reported to RUN RE, not reached for. This function's job is to stop destroying the
 * structure that DOES arrive; the day composition emits breaks, every answer gets them.
 *
 * ⚠️ SPLIT ON `\n{2,}`, NOT ON `\n`. Every newline run in the entire archived corpus is exactly
 * two long (measured: `{2: 6}` — six runs, all of length 2, across every prose string PQ captured).
 * A lone `\n` has never been observed, so treating one as a paragraph boundary would be a guess
 * about a shape nothing has produced; it keeps today's behaviour instead and costs nothing.
 * RUN SH re-ran that count over BOTH archives and it holds on both routes: 6 runs of length 2 here,
 * 103 runs of length 2 across the 112 research-route answers, and nothing of any other length.
 *
 * ✅ RUN SH · SH-2 — THE FUNCTION ITSELF NOW LIVES IN `packages/ui/answerParagraphs.mjs` AND IS
 * IMPORTED AT THE TOP OF THIS FILE. Everything above is RA's measurement and stands; what moved is
 * one copy of one rule, out of two apps that could not both own it. The rule's own reasoning — why
 * only `text` segments split, why the trim is edge-only — is documented once, in that module.
 */

function Prose({ segments, prose, quotes }) {
  const segs = segments?.length ? segments : [{ type: "text", text: prose || "" }];
  const ledger = quotes || [];
  /* 🔴 RUN CR · BLOCK 6 — LEGACY STORED ENVELOPES GET THE SENTENCE DROP TOO, and by the same
     shared function Research calls. A fresh answer never reaches here with an `unsupported`
     segment: the server removes the sentence before the envelope is written
     (apps/steel/server/lib/goodlaw/proseSupport.js `removeUnsupportedSentences`). A RESTORED turn
     re-renders the envelope that was stored, so the same remedy is applied on the read path. If
     the drop would empty the answer it is not applied and the `unsupported` branch below renders
     its quiet ellipsis. Adam: *"Every point ... should be the same for Clark and Research."* */
  const paras = toParagraphs(dropUnsupportedSentences(segs));
  const node = (s, i) => {
        if (s.type === "unresolved") {
          return (
            <mark key={i} className="cc-v2-unresolved" data-testid="clark-unresolved"
                  title="Named in the answer, but this authority did not resolve to a document in our corpus.">
              {s.text}
              <span className="cc-sr-only"> — not found in our corpus</span>
            </mark>
          );
        }
        // RUN C · PART 3 — named, and the lookup could not be completed. NOT the same statement as
        // `unresolved`: one is about the corpus, the other is about our availability.
        if (s.type === "unchecked") {
          return (
            <mark key={i} className="cc-v2-unchecked" data-testid="clark-unchecked"
                  title="Named in the answer. We could not complete the lookup for this authority just now, so we have neither confirmed nor ruled it out.">
              {s.text}
              <span className="cc-sr-only"> — we could not check this authority right now</span>
            </mark>
          );
        }
        /* ══ 🔴🔴 RUN CR · BLOCK 6 — A LEGACY PATH NOW, AND A QUIET ONE. ══════════════════════
           Adam: **"We should not withhold any quotations. Period."** then **"Fix the answer so it
           still makes sense. It can't look broken."** The remedy moved SERVER-SIDE and moved up a
           unit: `removeUnsupportedSentences` (steel goodlaw/proseSupport.js) now removes the whole
           SENTENCE the failed quotation was the support for, so a freshly composed answer carries
           no `{{unsupported:n}}` marker at all and this branch never runs on one.

           🔴 IT IS KEPT BECAUSE STORED ENVELOPES STILL CARRY THE MARKER. Rows written before
           tonight hold `{{unsupported:n}}` in their prose and are re-rendered by this component
           whenever an old turn is restored. Deleting this branch would fall those rows through to
           the default `<span>{s.text}</span>` — and an `unsupported` segment has NO `text` by
           construction (the withheld words are kept off the wire), so the sentence would silently
           close up with no sign anything was ever there. That is the silent omission, arriving
           through a deletion instead of a remedy.

           ⭐ SO: AN ELLIPSIS, which is the standard typography for omitted quoted material and the
           quietest mark that still holds the sentence together. The words `[quotation withheld]`
           and their screen-reader trailer are gone. The `title` survives for a reader who wants the
           reason. Research's `AnswerV2.jsx` carries the identical branch for the identical reason —
           Adam: *"Every point in this message / behavior should be the same for Clark and
           Research."* */
        if (s.type === "unsupported") {
          const q = Number.isInteger(s.index) ? ledger[s.index] : null;
          return (
            <span key={i} className="cc-v2-unsupported" data-testid="clark-unsupported"
                  title={q?.detail
                    ? `A quotation was removed here: ${q.detail}${q.caseName ? ` (${q.caseName})` : ""}`
                    : "A quotation stood here. It did not pass our support check against the source it was attributed to, so it was removed. The sentence is unchanged."}>
              …
            </span>
          );
        }
        /* 🔴 RUN TA — BLUEBOOK R2.1. A case name is italic, and until this run ours were not.
           `<em>`, not a styled `<span>`: the emphasis is SEMANTIC — a screen reader should stress a
           case name the same way a reader's eye does — and it means the italic survives a
           stylesheet that has not heard of `.cc-v2-casename`.
           🔴 THE CLIENT DECIDES NOTHING HERE. The span was chosen server-side against the RESOLVED
           authority list (clarkAnswerV2.js `caseNameNeedles`), which is the whole reason this is a
           segment type and not the regex-over-model-output this file has refused twice. An older
           client that has never heard of `casename` falls through to the default below and renders
           the same words in roman — a downgrade, never a `{{marker}}` leaking to a lawyer. */
        if (s.type === "casename") {
          return <em key={i} className="cc-v2-casename">{s.text ?? ""}</em>;
        }
        return <span key={i}>{s.text ?? ""}</span>;
  };
  return (
    <div className="cc-clark-section-prose cc-ans-prose" data-testid="clark-answer-prose">
      {paras.map((nodes, pi) => (
        <p key={pi} data-testid="clark-answer-para">{nodes.map((s, i) => node(s, `${pi}-${i}`))}</p>
      ))}
    </div>
  );
}

/**
 * ═══ PART 4 · A6 — THE THREE CONTROLS ════════════════════════════════════════════════════════
 *
 * Copy Answer · Open in Research · Show Authorities. Equal widths, each one third of the answer's
 * available width (CSS grid, `1fr 1fr 1fr` — see answerV2.css; three flex children with `flex:1`
 * would size to content and only LOOK equal until one label got longer).
 *
 * 🔴 NONE OF THESE IS AN OPTIONAL PROP THAT A CALLER MIGHT FORGET.
 * `Open in Research` was inert for three bundles because it depended on an `onOpenResearch` prop no
 * production caller passed — the control rendered identically whether or not it worked. That is the
 * codebase's own recorded lesson and this run's brief repeats it. So:
 *   · Copy Answer works off the payload already in this component. No handler needed.
 *   · Open in Research is an `<a href>` derived from the answer. If there is nothing to open, there
 *     is no href, and the control is disabled and SAYS why rather than silently no-op-ing.
 *   · Show Authorities is local state in this component.
 * A missing prop cannot make any of the three lie about being available.
 */
/**
 * ═══ RUN C · AMENDMENT — TWO OF THE THREE CONTROLS ARE CONDITIONAL ═══════════════════════════
 *
 * 🔴 THE ROW RENDERED UNCONDITIONALLY OVER STATES IT CANNOT ACT ON. Clark's zero-state offers six
 * action cards and two of them — *Generate a citation* and *Explain a Bluebook rule* — produce an
 * answer with NO authority list at all. `Open in Research` on a Bluebook-rule explanation loads an
 * empty authorities column and an empty reading surface into a three-pane workspace, and
 * `Show Authorities` opens an empty column. **A control that is present but does nothing meaningful
 * reads as broken, not as inapplicable** — the mirror image of this repo's own recorded finding
 * that a working mechanism with no entrance is indistinguishable from a broken one.
 *
 * HIDDEN, NOT DISABLED. A disabled button on a citation-formatting answer explains an absence the
 * reader was never expecting; it draws attention to a thing that was never applicable. (This is not
 * in tension with the disabled `Open in Research` below: THAT one appears on an answer that DOES
 * have authorities and could not be stored, which is a real, surprising absence worth stating.)
 *
 * 🔴 THE CONDITION IS READ OFF THE PAYLOAD, NEVER OFF THE RENDERED ROWS. `badge.resolved` is
 * counted over EVERY resolved authority including the ones past the 8-item display cap — the same
 * reason the badge itself counts beyond the cap. A gate computed from what is on screen would be
 * decided by the cap, which is a display decision, not a fact about the answer.
 *
 * 🔴 THE GEOMETRY DOES NOT MOVE. The grid stays `1fr 1fr 1fr` and a hidden control leaves its cell
 * EMPTY. Two buttons at half width each is a visibly different control row appearing on some
 * answers and not others; `Copy Answer` sits in the same third of the same row on every answer in
 * the transcript, which is what makes the row read as stable rather than as broken.
 */
function InteropRow({ answer, turnId, onOpenResearch, showing, onToggleAuthorities }) {
  const [copied, setCopied] = useState(false);
  const authorities = answer?.authorities || [];
  const openable = hasOpenableAuthorities(answer);

  // The text a user expects on the clipboard is what they can READ — the markers are our
  // bookkeeping. `segments` already has them separated, so this needs no parsing.
  const plain = (answer?.segments?.length
    ? answer.segments.map((s) => s.text).join("")
    : String(answer?.prose || "")).trim();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // A clipboard permission failure must not throw into the render tree. The label simply does
      // not change, which is the honest outcome: nothing was copied.
    }
  };

  // PART 4.3/4.5 — the destination, WITH the return address. researchHref is a pure function in
  // handoff.js precisely so "the round trip returns to the original message" is a claim a test can
  // make; see its header for why the handles are query params rather than shared storage.
  const href = openInResearchHref(answer, { turnId });

  return (
    <div className="cc-v2-actions" data-testid="clark-v2-actions">
      <button type="button" className="cc-v2-action" onClick={copy}
              data-testid="clark-action-copyanswer">
        {copied ? "Copied" : "Copy Answer"}
      </button>

      {/* The empty <span>s are the geometry. They occupy the grid cell and paint nothing, so the
          row is three equal thirds on every answer whether or not the last two controls apply. */}
      {!openable ? <span className="cc-v2-action-slot" aria-hidden="true" /> : href ? (
        /* 🔴 RUN OR — BOTH EVENTS, BECAUSE `auxclick` IS NOT `click`. A middle-click never fires
           `click`, so an onClick-only guard cannot see it; a ⌘-click does fire `click`, with
           `metaKey` set. The gate runs FIRST and returns true when it blocked the navigation, in
           which case the caller's handler must not run either — writing a handoff payload for a
           trip that is not happening would leave a stale payload for the next real one to adopt.
           Signed in, it returns false before touching the event and this is the anchor it was. */
        <a className="cc-v2-action cc-v2-action--accent" href={href}
           onClick={(e) => { if (blockResearchIfSignedOut(e)) return; onOpenResearch?.(e, answer); }}
           onAuxClick={(e) => { if (blockResearchIfSignedOut(e)) return; onOpenResearch?.(e, answer); }}
           data-testid="clark-action-research">
          Open in Research
        </a>
      ) : isSignedOut() ? (
        /* 🔴 RUN CK §4 TRIGGER A — SIGNED OUT, THIS IS LIVE, NOT DULLED. There is no `href` here
           because an ANONYMOUS answer is never stored, so the old branch rendered a permanently
           disabled control: the visitor was shown the destination and refused it with no way to
           earn it. It now opens the gate modal, which is the thing that WOULD make the destination
           exist. Signed in with no stored answer is genuinely nothing-to-open and stays disabled
           below — the two absences have different causes and different answers. */
        <button type="button" className="cc-v2-action cc-v2-action--accent"
                onClick={(e) => blockResearchIfSignedOut(e)}
                data-testid="clark-action-research">
          Open in Research
        </button>
      ) : (
        <button type="button" className="cc-v2-action cc-v2-action--accent" disabled
                title="This answer was not stored, so there is nothing for Research to open."
                data-testid="clark-action-research">
          Open in Research
        </button>
      )}

      {openable ? (
        <button type="button"
                className={"cc-v2-action cc-v2-action--accent" + (showing ? " is-on" : "")}
                aria-expanded={showing} aria-controls="cc-v2-authorities"
                onClick={onToggleAuthorities} data-testid="clark-action-authorities">
          Show Authorities
        </button>
      ) : <span className="cc-v2-action-slot" aria-hidden="true" />}
    </div>
  );
}

/**
 * One authority row.
 *
 * 🔴 HANDOFF 6.1 — `why` IS THE ANSWER'S OWN SENTENCE, AND IT IS LABELLED AS SUCH.
 * `whySource === 'answer_prose'` is rendered as "From this answer:" rather than as "what it
 * establishes". The old envelope could say the latter because the sentence had passed the gate.
 * This one cannot, and captioning model prose as a holding is the substitution
 * clarkAnswerShape.js:100 rejects in writing.
 */
/**
 * ═══ 🔴 RUN PJ — THE UNVERIFIED-AUTHORITY CARD, AND THE ONE RULE THAT MAKES IT SAFE ════════════
 *
 * THE ARTIFACT THIS REPLACES, STATED PLAINLY BECAUSE IT IS THE WORST THING THIS PRODUCT CAN
 * PRODUCE: the block below used to render `{s.name}` + a space + `{s.cite}`, hand-concatenated,
 * inside a sentence saying we could not verify that citation. RUN NB and RUN PA both proved the
 * reporter cite on these rows is MODEL-GENERATED — a row is in `stripped` or `unchecked`
 * PRECISELY BECAUSE IT DID NOT RESOLVE, so nothing from the corpus ever touched it. RUN PA marked
 * them `citeSource: 'model_asserted'` and left the display to Wave 2. This is it.
 *
 * MEASURED on the shipped renderer, RUN PY's captured Kennedy turn (ops/run-pj/shots/before-1440.png):
 *
 *     One authority named in this answer could not be checked just now …
 *       · American Legion v. American Humanist Association 588 U.S. 29
 *
 * No comma, no year — the Caron shape exactly, because it is string concatenation and not citation
 * formatting. A lawyer reads "588 U.S. 29" as a reporter citation. It is a number the model wrote.
 *
 * 🔴 THE RULE: NAME ONLY, UNLESS THE CORPUS SUPPLIED THE CITE.
 *   · `citeSource === 'model_asserted'` ⇒ the volume and page are NOT PRINTED. Not in the card,
 *     not in a tooltip, not in a `title` attribute — a tooltip is still a display.
 *   · When there is no name either, the cite is the only handle the reader has, so it is printed
 *     ATTRIBUTED — "the answer gave the citation X" — never as a bare citation. Never bare is the
 *     invariant; suppression is how it is satisfied when a name exists.
 *
 * ⚠️ AND EXPLICITLY NOT THE OTHER FIX. Adding the comma and the year would make an invented
 * citation look MORE authoritative. `rulebook.js` is not called here and must not be.
 *
 * 🔴 NON-CLICKABLE, DELIBERATELY. There is nothing to open — that is what put the row in this list.
 * The shared card renders `.cc-auth__hit.is-unlinked` when given neither `href` nor `onSelect`, so
 * this is the component's own no-dead-links path rather than a `<div>` dressed as a card.
 *
 * ⚠️ WHY THE BLOCK FIRES AT ALL, so the card does not read as an accusation. RUN PX is measuring
 * this. *Caron* is IN the corpus with 13 chunks and failed only because a 500-row candidate cap cut
 * its 805-row pool. Most of these are cases CiteClerk owns. The copy says we could not check it —
 * never that it does not exist.
 */
/* ═══ 🔴🔴 RUN VB — THIS COMPONENT IS UNMOUNTED. NOTHING RENDERS IT. ══════════════════════════
   Its two call sites — the `stripped` list and the `unchecked` *See also* group — were deleted
   from this file by RUN VB. It is kept, deliberately and as a declared judgment call, for ONE
   reason: the rule immediately below it.

   ⭐ A MODEL-ASSERTED CITE IS NEVER PROMOTED TO A HEADING. `citeSource === "model_asserted"` on
   every row that reaches this card, and that is the condition under which a citation the model
   INVENTED would otherwise be printed in the same slot, same weight, as one the corpus supplied.
   RUN NB and RUN PA each paid for a version of that; the rule is written on the FIELD rather than
   assumed from the list, so the day a corpus-supplied cite legitimately lands here the card starts
   printing it.

   🔴 RUN VC — THIS IS ADDRESSED TO YOU. You rebuild these cards in the RAIL, off the same
   `stripped` / `unchecked` / `citeSource` fields (RUN VB touched none of them). Carry this rule
   into the rail card, then DELETE this function. Deleting the reviewed implementation in the same
   run that asks a sibling to rebuild it is how a suppression comes back from memory instead of
   from review — which is the whole reason it is still here and not in the git history.

   ⚠️ It renders nothing today. If you are reading this in a run that is not VC, and the rail card
   carries the rule, delete it — dead code with an expired owner is just dead code. */
function UnverifiedAuthorityCard({ s }) {
  const modelAsserted = s.citeSource === "model_asserted";
  /* The corpus never touched an unresolved row, so `model_asserted` is expected on every one. The
     condition is written on the FIELD rather than assumed from the list, because the day a
     corpus-supplied cite legitimately lands here, this card must start printing it. */
  const corpusCite = s.cite && !modelAsserted ? s.cite : null;

  return (
    <SharedAuthorityCard
      as="li"
      testId="clark-v2-seealso-card"
      legacyClass="cc-v2-seealso-card"
      /* Name first. A corpus-supplied cite may be promoted to the heading; a MODEL-ASSERTED one
         never is — that promotion is exactly how a suppressed cite would come back as a heading. */
      name={s.name || corpusCite || "An authority we could not identify"}
      cite={s.name ? corpusCite : null}
      /* 🔴 EVERY OTHER SLOT IS LEFT EMPTY ON PURPOSE, NOT FOR WANT OF DATA. No type badge, no
         court, no year, no citing count, no why — Adam's card ruling applies here too, and on a
         card about an authority we could not check there is nothing we are entitled to add.
         No `href` and no `onSelect`: there is nothing to open, which is what put the row in this
         list, and the shared card's own no-dead-link path renders `.cc-auth__hit.is-unlinked`. */
      unlinkedNote={
        !s.name && s.cite
          /* Nameless: the citation is the only handle the reader has, so it is ATTRIBUTED in words
             rather than suppressed into a blank card. This is the "never bare" half of the rule —
             suppression satisfies it when a name exists, attribution when one does not. */
          ? `The answer gave the citation “${s.cite}”. We could not verify it.`
          : null
      }
    />
  );
}

function AuthorityRow({ a, onAskAbout }) {
  /* ══ 🔴🔴 RUN CR · BLOCK 7 — THIS LIST IS CARDS NOW, AND THEY ARE RESEARCH'S CARDS. ═══════════
     ADAM: *"The authorities for Clark need to be in cards and need to have the source chips.
     Needs to look 1:1 with Research except for no highlights in Clark."*

     WHAT STOOD HERE: a hand-rolled `<li>` — a rank number, a bare name, a cite and a small
     `Case`/`Statute` word — with its own `.cc-v2-auth*` type scale. Research's rail renders
     `@citeclerk/ui`'s `.cc-auth` (white fill, 1px edge, 10px radius, `--cc-shadow-md`) with the
     glassy source chip inside the top of the card. Two products, one payload, two cards.

     ⭐ THE SHARED COMPONENT, NOT A RE-STYLE. `packages/ui/react/AuthorityCard.jsx` is frozen for
     this run and is NOT edited: the chip is reached the way RUN VA and RUN RD · R7 both reached it,
     by putting `data-kind` on the card and letting `answer.css` skin `.cc-auth__type`. `type` is
     the same `Case`/`Statute` fold this row already computed, moved into the card's own slot.

     🔴 "EXCEPT FOR NO HIGHLIGHTS IN CLARK" IS HONOURED BY WHAT IS **NOT** PASSED. Research's rail
     rows take `interactive="container"`, `role="option"`, `aria-selected` and `selected`, because
     over there a card is a SELECTION that drives the reading pane. There is no reading pane here
     and nothing to select, so none of those props is passed and no row can take a selected state.
     A card that highlights on click and then does nothing is the inert-control defect this file
     has already paid for once.

     ⚠️ THE RANK NUMBER GOES. Research shows no rank, and the card has no slot for one; keeping it
     would be the single thing still making these two lists look different. The ORDER is unchanged
     — the list is still rendered in the payload's own order — so nothing about precedence is lost,
     only a numeral that Research never drew.

     ✅ AND THE QUESTION ABOVE THE LIST STAYS. Adam: *"the authorities list in Clark should show the
     question up top and it shouldn't in research."* It is `.cc-v2-authorities-q`, rendered by the
     caller just above this list, and it is untouched — see PART 4.6's note there for why the column
     needs it (it can hold a different answer's list a moment later). Research's rail head is
     `Authorities` plus a count and prints no question; measured, not assumed. */
  const isStatute = a.kind === "statute";
  return (
    <li className="cc-v2-auth" data-testid="clark-v2-authority" data-kind={isStatute ? "statute" : "case"}
        data-why={a.why ? "present" : "absent"}>
      <SharedAuthorityCard
        as="div"
        legacyClass="cc-ans-card"
        testId="clark-v2-authority-card"
        data-kind={isStatute ? "statute" : "case"}
        type={isStatute ? "Statute" : "Case"}
        name={a.name || a.cite}
        cite={a.name && a.cite ? a.cite : null}
        href={a.href || undefined}
        why={a.why ? (
          <><span className="cc-v2-auth-why-label">From this answer:</span> {a.why}</>
        ) : (
          <><span className="cc-v2-auth-why-label">Listed as relied on;</span> not named in the answer’s text.</>
        )}
      />
      {/* ═══ RUN 9 · PART 6 — WHAT THIS SLOT SHOWS WHEN `why` IS NULL ═════════════════════════
          `why` is the answer's own sentence naming this authority, and it is null for an authority
          the model listed but never wrote into the prose. RUN 5's live turn measured that at 7 of 7
          — this is the COMMON shape, not the edge case, so a slot that renders nothing renders
          nothing most of the time.

          Three options; two are refused:
            · a matched chunk from the opinion — REFUSED. `whySource` exists precisely so no surface
              can caption retrieved text as this answer's reasoning (clarkAnswerShape.js:100).
            · a model-written justification — REFUSED. That is inventing a holding.
            · SAY WHERE IT CAME FROM. Kept, because it is a true and checkable statement about the
              payload, and it tells the reader the thing they actually need: the prose does not rest
              on this row, so there is no sentence to go looking for. */}
      {/* RUN CR · BLOCK 7 — `why` MOVED INTO THE CARD'S OWN SLOT (`.cc-auth__why`), which is the
          slot packages/ui built for exactly this and which Research leaves empty because it has no
          honest "why" to put there. Both branches survive word for word, including the ABSENT one:
          RUN 9 · PART 6's argument above is unchanged and is the reason there are two. The marker
          for the absent branch rides on the row instead of a deleted `<p>`. */}
      {/* RUN 9 · PART 5.1 — the one place a CASE target can come from without guessing. This row's
          `clusterId` was resolved by the answer that produced it, so asking about it needs no
          lookup and no retrieval. Rendered only when a caller wired it: a control that cannot work
          must not appear (this codebase already paid once for an inert `Open in Research`). */}
      {/* OUTSIDE the card, deliberately: the shared card has exactly one hit area and its body must
          not contain a nested control (packages/ui/react/AuthorityCard.jsx's `interactive` note).
          Rendered only when a caller wires it — Clark's index.jsx does. */}
      {onAskAbout ? (
        <button type="button" className="cc-v2-auth-ask" data-testid="clark-v2-ask-about"
                onClick={() => onAskAbout(a)}
                title="Ask a question answered from this authority’s own text">
          Ask about this authority
        </button>
      ) : null}
    </li>
  );
}

/**
 * ═══ 🔴🔴 RUN RA — THE DECLINE, WHICH UNTIL NOW RENDERED ZERO CHARACTERS ══════════════════════
 *
 * MEASURED at HEAD, on all five of RUN PQ's archived blocking envelopes, mounted in real Chrome
 * (`ops/run-ra/out/before-decline-*.json`):
 *
 *     [data-testid="clark-answer-prose"]   present: true    chars: 0    paragraphs: 0
 *     authority cards in the rail          4 · 5 · 4 · 0 · 0
 *
 * `Prose` above does `segments?.length ? segments : [{ type:"text", text: prose || "" }]`, and a
 * blocking envelope has `prose: ""` and `segments: []`. So the fallback fires, renders the empty
 * string, and the reader gets AN EMPTY BUBBLE beside a full authorities rail. RUN PA built the
 * decision — `blocking`, `grounding.declineKind`, `grounding.readInstead`, `withheldProse` — and
 * grepped across both clients and the shipped `dist` bundles there were, at HEAD, ZERO consumers.
 *
 * ⚠️ THE BRIEF PUTS `declineKind` AND `readInstead` AT THE TOP OF THE ENVELOPE. THEY ARE NOT THERE.
 * The shipped blocking envelope has exactly 20 keys and carries `blocking` and `withheldProse` at
 * top level; the kind and the suggestions live INSIDE `grounding` (clarkAnswerV2.js:363 ships
 * `grounding` whole, and groundingGate.js:186-249 builds it). Reading them off `answer.` would have
 * been `undefined` on every real decline and this component would have shipped a decline with no
 * kind and no suggestions — passing its own tests, because the fixtures would have matched the
 * mistake. Read off `answer.grounding`.
 *
 * 🔴 WHAT ELSE HAD TO GO, AND WHY IT IS NOT SCOPE CREEP. The empty bubble was not the only thing on
 * screen. MEASURED on `before-decline-RQ01`, the assistant row read, in full:
 *
 *     "Named in this answer. We could not check it just now…"
 *     "The answer gave the citation “Restatement (Second) of Contracts § 90”. We could not verify it."
 *     "How we know: 4 authorities resolved to a document in our corpus · …"
 *     "None of this is a check that the answer is correct."   [Copy Answer] [Open in Research]
 *
 * Every one of those sentences says THIS ANSWER about an answer that was refused and does not
 * exist. The brief asks that the "CiteClerk Verified" badge not sit on a decline; the badge itself
 * was already gone (RUN D removed it by measurement, see the top of this file). Its SUCCESSOR is
 * the defect: `HowWeKnow` prints a verification ledger — resolved counts, quotation counts — over
 * nothing at all, which is the same lie in more words. So a decline renders NO `HowWeKnow`, NO
 * stripped/see-also block, and NO Copy/Open row. There is no answer to vouch for, cite, copy or
 * open.
 *
 * ⭐ RUN VB — READ THIS AS HISTORY, AND NOTE WHICH HALF OF IT SURVIVED. The ledger and the two
 * See-also blocks are now absent from the ANSWER branch too, so the first two exclusions above are
 * no longer this branch's distinction; they are the product's. The Copy/Open row still is: an
 * answer keeps its three controls, a decline has nothing to copy or open. The quoted strings above
 * are what `before-decline-RQ01` PRINTED, kept verbatim because the measurement is the argument.
 *
 * 🔴 `withheldProse` IS NEVER RENDERED — not here, not in a title, not behind a disclosure, not in
 * an aria-label. It is prose the gate refused, and RUN PB2 drops it from the snapshot for exactly
 * this reason. It is not read by this component at all; that is the only implementation of "never"
 * that cannot rot.
 *
 * ⭐ THE REGISTER IS THE PRODUCT WORKING, NOT AN APOLOGY. A decline is the one moment CiteClerk
 * does the thing its competitors cannot: it declines to write a confident wrong answer. It should
 * not read like an error, and it must never read like evasion.
 */

/* 🔴 THE TWO KINDS NEVER COLLAPSE — ADAM'S RULING, AND THE COPY IS THE WHOLE OF IT.
   `groundingGate.js:189-201` decides: `capability` when composition produced NOTHING and nothing
   resolved; `no_record_support` everywhere else. Those are different facts about the world and a
   lawyer needs to know which one they are looking at:

     no_record_support  "the record does not support this"     — a statement about the CORPUS
     capability         "this is outside what Clark does"      — a statement about the PRODUCT

   ⭐ RUN TA (steel) — THE THREE STRING TRIPLES THAT STOOD HERE ARE NOW `@citeclerk/ui/declineCopy`,
   VERBATIM AND UNEDITED. Not a tidy-up: RESEARCH PAINTED ZERO CHARACTERS on the same envelope this
   component renders correctly (*Calloway v. Meridian Freight Systems*, `blocking: true`,
   `prose: ''` — RUN SC), and Research's own `HonestDecline.jsx` keyed its two strings on
   `cases.length > 0`, which is the surface guessing a reason the gate had already emitted. Two
   implementations of one rule is the defect; a shared module is the fix, and it is the same fix
   `howWeKnow.mjs` already is for the sentence below the answer.

   🔴 THE RULING SURVIVES THE MOVE AND IS RESTATED THERE IN FULL: paraphrase loses the difference,
   so the two strings are written out separately and neither is built by interpolating into the
   other; and `null` — a blocking envelope with no kind — is NOT folded into `no_record_support`,
   because claiming the corpus was searched and came up short is a claim, and an absent kind does
   not support it. All five archived declines carry `no_record_support`; the `capability` fixture is
   generated by calling the real gate (`ops/run-ra/fixtures/_make-capability.mjs`). */

/* ⚠️ NAMING THE JURISDICTION IS A CLAIM, AND `jurisdictionLabel` WOULD HAVE OVER-CLAIMED IT.
   `grounding.searched.jurisdiction` is "CA" — UPPERCASE, from the prompt extractor — while the
   picker's own id for California is lowercase "ca". `jurisdictionLabel` falls back to
   "All jurisdictions" for anything it cannot find, so a decline on a CALIFORNIA search would have
   told the reader the whole corpus was searched and came up short. That is BUNDLE V7 · LANE 4
   RULE 1, verbatim, arriving through a second door: "an unrecognised id must not render as
   'All jurisdictions'".
   So: lowercase first (the id IS California, only cased differently), resolve through
   `scopeStampLabel` — which returns null rather than guessing — and when it returns null, say
   nothing about jurisdiction at all. No stamp is honest; a confident wrong one is not. */
/* 🔴🔴 RUN TA (steel) — AND `null` MEANT TWO THINGS HERE, WHICH IS HOW A LATENT OVER-CLAIM GETS
   COPIED INTO A SECOND APP. `scopeStampLabel` returns null for BOTH "the search was not narrowed"
   AND "this id did not resolve" — its own header cites BUNDLE V7 · LANE 4 RULE 1 for the second
   ("an unrecognised id must not render as 'All jurisdictions'") — and the render below did
   `where || "all jurisdictions"`, which turns both of them into a claim that the WHOLE CORPUS was
   searched.

   ⚠️ POPULATION ZERO, AND THAT IS STATED RATHER THAN GLOSSED. Across all 50 archived envelopes
   (`apps/steel/ops/run-ta/step0-population.mjs`) `grounding.searched.jurisdiction` takes four
   values — `all` ×46, `CA` ×2, `DE`, `WY` — and every one of them resolves. So NO PIXEL MOVES
   TODAY. What changes is that the two nulls are now separate values, `unscoped` is passed
   explicitly to the shared clause builder, and the ambiguity cannot cross the seam into Research.
   No stamp is honest; a confident wrong one is not. */
function searchedLabel(searched) {
  const raw = String(searched?.jurisdiction ?? "").trim();
  const unscoped = !raw || raw.toLowerCase() === "all";
  return { where: unscoped ? null : scopeStampLabel(raw.toLowerCase()), unscoped };
}

function Decline({ answer }) {
  const g = answer.grounding || null;
  /* 🔴 RUN TA (steel) — THE KIND AND THE COPY COME FROM THE SHARED MODULE, AND SO DOES THE RULE FOR
     PICKING BETWEEN `body` AND `bodyNothingFound`. `declineOf` reads `blocking === true` first and
     on EQUALITY — see its header for why the ordering is load-bearing — and returns the resolved
     strings, so this component no longer holds a copy of either decision. */
  const d = declineOf(answer);
  const kind = d?.kind || "unknown";
  const retrieved = Number(g?.searched?.authoritiesRetrieved ?? 0);
  const { where, unscoped } = searchedLabel(g?.searched);
  /* 🔴 THE ROWS ARE CORPUS-RESOLVED, NOT MODEL-ASSERTED, AND THAT IS WHY THEY MAY CARRY A CITE.
     `groundingGate.js:246` filters `readInstead` on `corpusTraceable` — a case needs a `clusterId`,
     a statute needs a cite AND an excerpt. This is the OPPOSITE provenance from `stripped` and
     `unchecked`, whose reporter cites RUN NB and RUN PA both proved are model-generated and which
     RUN PJ's card exists to stop displaying as citations. Printing these is safe for the reason
     printing those is not. The `href` is joined from `answer.authorities` by id — the gate does not
     carry one, and a suggestion you cannot open is half a suggestion. */
  const byId = new Map((answer.authorities || []).map((a) => [a.id, a]));
  const readInstead = (g?.readInstead || []).map((r) => ({ ...r, href: byId.get(r.id)?.href || null }));

  /* ⭐ "(from your search)" IS CARRIED OVER FROM `ProvenanceLine`, WHICH THIS BRANCH CANNOT RENDER.
     That component's line reads `Answered for CA (from your search)` — and MEASURED on
     `before-decline-RQ14` it printed exactly that above a turn that was NOT answered. It is the
     same class of sentence as the HowWeKnow ledger: chrome asserting an answer exists. So the
     decline does not render it, and the half of it that is still TRUE — where the jurisdiction came
     from — moves onto the line above, which says `Searched`.

     ⚠️ Appended ONLY when the gate's `searched.jurisdiction` and the envelope's `effective` agree.
     They are two fields filled by two different parts of the pipeline; attributing the picker as
     the source of a jurisdiction the gate did not actually search would be a confident wrong claim
     about provenance, which is the thing this whole surface exists to avoid. */
  const eff = String(answer.jurisdiction?.effective ?? "").trim().toLowerCase();
  const src = answer.jurisdiction?.source ?? null;
  const agree = eff && eff === String(g?.searched?.jurisdiction ?? "").trim().toLowerCase();
  const sourceSuffix = !agree ? ""
    : src === "prompt" ? " (from your search)"
    : src === "picker" ? " (from your jurisdiction picker)" : "";

  return (
    <div className="cc-ans-decline" data-testid="clark-decline" data-decline-kind={kind} role="note">
      <p className="cc-ans-decline-head">{d.head}</p>
      <p className="cc-ans-decline-body">{d.body}</p>

      {/* ⭐ NAME WHAT WAS SEARCHED. A decline that does not say what it looked at is indistinguishable
          from a decline that did not look, and the second is the one a reader will assume. The count
          is the gate's own denominator (`grounding.searched.authoritiesRetrieved`), not a count of
          what happens to be on screen. Rendered only when there is something true to say. */}
      {searchedClause({ retrieved, where, unscoped, sourceSuffix }) && (
        <p className="cc-ans-decline-searched" data-testid="clark-decline-searched">
          {searchedClause({ retrieved, where, unscoped, sourceSuffix })}
        </p>
      )}

      {/* 🔴 THE AUTHORITIES STILL RENDER — retrieval succeeded, composition did not, and a decline
          beside the things the search actually found is a far better artifact than a decline beside
          nothing. Two of the five archived declines DO have an empty list (RQ14, RQ16 retrieved 0),
          and this block correctly renders nothing for them rather than an empty heading. */}
      {readInstead.length > 0 && (
        <div className="cc-ans-decline-read" data-testid="clark-decline-read">
          <h3 className="cc-ans-decline-read-head">Start here instead</h3>
          <ul className="cc-ans-decline-read-list" role="list">
            {readInstead.map((r) => (
              <li key={r.id}>
                {r.href
                  ? <a href={r.href} className="cc-ans-decline-read-link">{r.name || r.cite}</a>
                  : <span className="cc-ans-decline-read-link">{r.name || r.cite}</span>}
                {r.name && r.cite ? <cite className="cc-ans-decline-read-cite">{r.cite}</cite> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴🔴 RUN TA — THE REVEAL. THE PROSE DRAINS IN; IT DOES NOT DUMP.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⭐ THE ONE FACT THAT MAKES THIS A MOUNT ANIMATION AND NOT A TRANSITION. This component does not
 * exist during the turn. `thread/Turn.jsx:76` dispatches on `answer.kind === 'clark.answer.v2'`,
 * and `kind` is set exactly once — when `done` replaces the live object (`answerApi.js`'s v2 note).
 * Before that the turn renders `ResearchAnswer`'s streaming branch: `TypingDots` and the stage
 * label, with the retrieved set already in the RAIL. So there is no half-state to detect, no
 * streamed prefix to reconcile, and no "did this already animate" question to get wrong across a
 * re-render: ClarkAnswerV2 MOUNTS with the complete, gate-passed answer, and the reveal is simply
 * what it does on the way in.
 *
 * ── 🔴 THE THREE CASES THAT MUST NOT ANIMATE, AND WHY EACH ONE IS A DIFFERENT ARGUMENT ─────────
 *
 *   `restored: true`   A REOPENED CHAT IS NOT AN ARRIVING ANSWER. `chatsApi.js:97` stamps this on
 *                      every turn it rehydrates, for exactly this class of decision. Animating it
 *                      would perform the writing of an answer that was written days ago — the
 *                      surface telling the user something is happening when nothing is. It is
 *                      checked FIRST, before the motion preference, so the reason on screen is the
 *                      true one.
 *   reduced motion     The OS preference, read through the same helper `VerificationTrace` uses
 *                      (now exported rather than copied). A reveal is decorative motion by
 *                      definition; there is no information in it that the final text does not
 *                      carry, so it is the easiest thing on this surface to switch off.
 *   `blocking`         A DECLINE IS NOT SUSPENSE. This needs no branch and gets none: a blocking
 *                      envelope has `prose: ''` and `segments: []` by construction
 *                      (clarkAnswerV2.js empties both rather than marking them), so the total is
 *                      zero and the reveal is complete before its first frame. ⭐ It is ALSO
 *                      unreachable, because `answer.blocking` returns above the prose — belt and
 *                      braces, and stated here so nobody later "fixes" the empty-total case into
 *                      something that animates a refusal.
 *
 * ── THE CLOCK ────────────────────────────────────────────────────────────────────────────────
 * `requestAnimationFrame`, not `setInterval`. Three reasons and all of them are correctness, not
 * taste: rAF is throttled by the browser in a background tab, so a user who switches away does not
 * come back to an answer mid-drain; it is naturally cancelled by the effect cleanup, so an unmount
 * cannot orphan a timer; and it paces off the WALL CLOCK rather than off a tick count, so the
 * stated duration is the real duration on a slow frame instead of drifting long.
 *
 * ── THE INTERRUPTS ───────────────────────────────────────────────────────────────────────────
 * All three snap to the FULL text — never freeze in place, which would leave a permanently
 * half-written answer, and never clear.
 *   scrolling up   the reader has left the live edge; they are reading, not watching. Detected on
 *                  the thread scroller (`[data-testid="clark-stream"]`) rather than on window, and
 *                  by DECREASING scrollTop plus a negative wheel, so the auto-scroll this very
 *                  reveal causes as the block grows — which only ever moves scrollTop UP — cannot
 *                  cancel it.
 *   sending again  a `MutationObserver` on the turn list — A NEW TURN APPEARING BELOW THIS ONE ends
 *                  its reveal. 🔴 IT IS DELIBERATELY NOT A CALL FROM `send()`, and that is a scope
 *                  finding, not a preference: `index.jsx` carries RUN TC's UNCOMMITTED rebuild of
 *                  the grounded predicate inside that exact function, and this run's own brief
 *                  gives `send()`'s grounded branch to TC. A one-line call there would have to be
 *                  committed with TC's unfinished work or not at all. Observing the DOM instead
 *                  costs nothing, is strictly more general (any path that appends a turn ends the
 *                  reveal, not just the one `send()` knows about), and leaves this run owning zero
 *                  lines of a file a sibling is editing.
 *   switching chat  no code at all: every turn is replaced, this component unmounts, the cleanup
 *                  below cancels the frame, and the replacements carry `restored: true`.
 */
function useReveal(answer) {
  const rootRef = useRef(null);
  /* Normalised ONCE, here, so the reveal and the renderer agree about what a character is. `Prose`
     falls back to `[{type:'text', text: prose}]` when `segments` is empty; if the reveal did not do
     the same, a truncated-to-empty segment list would hit that fallback and paint the WHOLE answer
     on the first frame — the dump, arriving by the door built to prevent it. */
  const source = answer?.segments?.length
    ? answer.segments
    : (answer?.prose ? [{ type: "text", text: answer.prose }] : []);
  const total = revealTotal(source);
  const instant = Boolean(answer?.restored) || total === 0 || prefersReducedMotion();
  const [shown, setShown] = useState(() => (instant ? total : 0));

  useEffect(() => {
    if (instant) { setShown(total); return undefined; }
    let raf = 0;
    let t0 = 0;
    let stopped = false;
    const dur = revealDurationMs(total);
    const snap = () => {
      if (stopped) return;
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      setShown(total);
    };
    const step = (t) => {
      if (stopped) return;
      if (!t0) t0 = t;
      const p = Math.min(1, (t - t0) / dur);
      setShown(Math.round(total * p));
      if (p < 1) raf = requestAnimationFrame(step); else stopped = true;
    };
    raf = requestAnimationFrame(step);

    /* 🔴 childList ONLY, and NOT `subtree`. The reveal's own work is a text mutation deep inside
       this turn; with `subtree: true` the observer would hear itself on frame one and snap
       instantly — a feature that cancels itself, and one that would have looked exactly like "the
       reveal does not work" rather than like a bug in its interrupt.
       ⚠️ AND ONLY A TURN COUNTS. `index.jsx` also appends the generic typing row as a sibling of
       these rows; treating that as "the user sent again" would end the reveal on a frame where
       `sending` and `streamingTurnId` briefly disagree. The filter is the row's own testid. */
    const myTurn = rootRef.current?.closest('[data-testid="clark-turn"]') || null;
    const turnList = myTurn?.parentNode || null;
    const isTurn = (n) => n.nodeType === 1
      && (n.matches?.('[data-testid="clark-turn"]') || !!n.querySelector?.('[data-testid="clark-turn"]'));
    const mo = turnList && typeof MutationObserver !== "undefined"
      ? new MutationObserver((recs) => {
          for (const r of recs) for (const n of r.addedNodes) if (isTurn(n)) { snap(); return; }
        })
      : null;
    mo?.observe(turnList, { childList: true });

    const scroller = rootRef.current?.closest('[data-testid="clark-stream"]') || null;
    let lastTop = scroller ? scroller.scrollTop : 0;
    // A 4px dead band: a sub-pixel settle after a layout change is not a reader scrolling up.
    const onScroll = () => { const top = scroller.scrollTop; if (top < lastTop - 4) snap(); lastTop = top; };
    const onWheel = (e) => { if (e.deltaY < 0) snap(); };
    if (scroller) {
      scroller.addEventListener("scroll", onScroll, { passive: true });
      scroller.addEventListener("wheel", onWheel, { passive: true });
    }
    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      mo?.disconnect();
      if (scroller) {
        scroller.removeEventListener("scroll", onScroll);
        scroller.removeEventListener("wheel", onWheel);
      }
    };
  }, [answer, total, instant]);

  const revealing = shown < total;
  return {
    rootRef,
    revealing,
    /* 🔴 WHEN THE REVEAL IS OVER THE ORIGINAL PROPS ARE PASSED THROUGH UNTOUCHED — not a rebuilt
       equivalent of them. A finished answer must be byte-identical to what this component rendered
       before this run existed, so that every measurement RA took against it still holds and so a
       bug in the pacing cannot survive into the resting state. `prose: ""` while revealing is the
       other half of the fallback note above. */
    segments: revealing ? revealSegments(source, shown) : answer?.segments,
    prose: revealing ? "" : answer?.prose,
  };
}

export default function ClarkAnswerV2({ answer, turnId, onOpenResearch, onFeedback, onAskAbout }) {
  /* 🔴 RUN CX — mapped ONCE per answer, not per render. `railPayload` builds the whole rail payload
     and only its `seeAlso` array is wanted here; it is a pure mapping over the envelope, so the memo
     key is the envelope itself. */
  const mobileSeeAlso = useMemo(() => railPayload({ id: turnId, answer }).seeAlso, [answer, turnId]);
  // PART 4.6 — the list is shown on demand, per answer. Default closed: the authorities also live
  // in the rail, and opening every answer's list by default would put the same eight rows on screen
  // twice, which is the duplication V7 Lane 6 removed from the old path.
  const [showing, setShowing] = useState(false);
  /* 🔴 CALLED UNCONDITIONALLY AND BEFORE EVERY EARLY RETURN — the `!answer`, the `blocking`
     decline and the answer branch below all pass through here. A hook after a conditional return
     is the one React rule that fails as a corrupted render rather than as an error, and this
     component has three exits. `useReveal` handles a null `answer` itself (total 0 ⇒ instant). */
  const reveal = useReveal(answer);
  if (!answer) return null;

  /* ══ 🔴 THE DECLINE BRANCH, AND WHY IT IS *HERE* AND NOT IN `Turn` OR `index.jsx`. ═════════════
     The brief flags `index.jsx`'s `r.status === "answer" || r.status === "decline"` as handling the
     two identically. It does, and — measured — that is CORRECT rather than the defect: `status` is
     computed at `answerApi.js:467` as `!data.sentences?.length`, and `sentences` is a LEGACY v1
     field that NO v2 envelope carries. So `status` is `"decline"` on every v2 turn ever served,
     delivered ones included. It could never have driven this surface. The only truth is
     `answer.blocking`, which the server sets in one place and says no client may overrule
     (clarkAnswerV2.js:352-358). So `index.jsx` is not touched, and neither is `Turn`.

     ⭐ AND IT IS ABOVE EVERYTHING, NOT A BRANCH INSIDE THE BODY. Amendment A3 put the advice notice
     in `Turn` on the reasoning that a thing which must be remembered in seven render branches will
     be missing from one. Same rule: one early return, so there is no path on which a decline
     acquires an answer's chrome. Everything below this line — the prose, Copy Answer, Open in
     Research — is unreachable on a blocking envelope, which is the point. (RUN VB: the see-also
     cards and the HowWeKnow ledger used to be on that list. They are not unreachable now, they are
     GONE — from this branch and from the answer branch alike.) */
  if (answer.blocking) {
    return (
      <div className="cc-ans cc-ans-v2" data-testid="clark-answer-v2" data-state="decline">
        {/* 🔴 THE NOTICE ONLY. `ProvenanceLine`'s other branch says "Answered for CA", which is false
            here — measured on `before-decline-RQ14`, printed above a turn with no answer in it. The
            `notice` branch is a jurisdiction CONFLICT warning and is as true on a decline as on an
            answer, so it stands; the "Answered for" branch is suppressed and its surviving half
            (where the jurisdiction came from) is stated by `Decline`'s own `Searched …` line. */}
        {answer.jurisdiction?.notice ? <ProvenanceLine jurisdiction={answer.jurisdiction} /> : null}
        <Decline answer={answer} />
      </div>
    );
  }

  const stripped = answer.stripped || [];
  const unchecked = answer.unchecked || [];
  const notice = answer.jurisdiction?.notice || null;
  /* 🔴 RUN TA (steel) — computed AFTER the `blocking` early return above, so `declineOf` can only
     return the PROSE mode here. `d.mode === 'prose'` is asserted rather than assumed: the module
     tests `blocking` first and on equality, and a change to that ordering must break this, not
     silently start annotating an empty envelope. */
  const decline = declineOf(answer);
  const proseDecline = decline && decline.mode === "prose" ? decline : null;

  /* `data-revealing` puts the reveal's own state ON THE ELEMENT, so a Playwright assertion and a
     frame-stamped screenshot can both read it without reaching into React. It is what the drive
     polls, and it is the reason the interrupt tests can assert a SNAP rather than a freeze.
     ⚠️ AND THE COMMENT SITS HERE, ABOVE THE `return`, NOT INSIDE THE TAG. RUN SC paid for the
     other shape at `AuthorityCard.jsx:432`, where a block comment placed directly inside JSX became
     a TEXT CHILD and crushed the Clark composer to 33px. Writing this note cost a compile error of
     its own on the first attempt — a block comment that quotes a JSX comment closes itself early —
     which is a small, exact demonstration of why the rule is "comments about JSX go above it". */
  return (
    <div className="cc-ans cc-ans-v2" data-testid="clark-answer-v2" data-state="answer"
         ref={reveal.rootRef}
         data-revealing={reveal.revealing ? "true" : "false"}
         aria-busy={reveal.revealing ? "true" : undefined}>
      {/* ITEM 7 — the prompt-beats-picker notice. Non-blocking, above the prose, never a question.
          HANDOFF 6.2 re-homes Research's two orphaned provenance notices into this same line —
          see ProvenanceLine below. */}
      <ProvenanceLine jurisdiction={answer.jurisdiction} />

      {/* ══ 🔴🔴 RUN TA (steel) — THE SECOND DECLINE, THE ONE THAT ACTUALLY FIRES. ════════════════
          `blocking` — the branch above — is **0 of RA2's 36 archived probes**. Every decline RA2
          scored came through HERE: composition SUCCEEDED, the gate is satisfied, and what the model
          composed is a refusal. RUN SC gave that fact a name (`declined`, `answerDecline.js`) and
          nothing on either client had read it. A renderer that covers only `blocking` covers the
          case that never happens.

          ⭐ IT ANNOTATES; IT DOES NOT REPLACE. The refusal is already written, below this note, in
          the model's own words — 523 to 2,139 characters of it across the 8 archived instances. A
          second refusal printed over the top would be the surface talking over its own payload, and
          "in whole or in part" is load-bearing: C30 refuses one regulation verbatim and then answers
          for three paragraphs.

          🔴 IT SITS ABOVE THE PROSE, NOT BELOW IT, AND THAT IS ADAM'S RULE FROM RUN VB TEN LINES
          DOWN: *"No content should be under the answer."* It is also the only correct place for it
          — a caveat read after the thing it qualifies is not a caveat. RUN SH measured the same
          sentence below its cards and found it below the fold in 8 of 9 viewport/fixture pairs.

          ⚠️ AND IT RIDES A CLASSIFIER, SO THE FAILURE DIRECTION IS DECLARED: a false positive prints
          a note that says LESS than the answer does and asserts nothing about the world; a false
          negative leaves today's behaviour exactly as it is. Neither can produce a new claim.
          `disclaimedClause` is rendered only when `badge.disclaimed > 0` — the B19 shape, 2 of 178
          authority rows — in the register SC's own card uses, so the two cannot come to disagree. */}
      {proseDecline && (
        <div className="cc-ans-decline cc-ans-decline--prose" data-testid="clark-decline-prose"
             data-decline-kind="prose" role="note">
          <p className="cc-ans-decline-head">{proseDecline.head}</p>
          <p className="cc-ans-decline-body">{proseDecline.body}</p>
          {disclaimedClause(answer.badge) && (
            <p className="cc-ans-decline-searched" data-testid="clark-decline-disclaimed">
              {disclaimedClause(answer.badge)}
            </p>
          )}
        </div>
      )}

      <Prose segments={reveal.segments} prose={reveal.prose} quotes={answer.quotes} />

      {/* ═══ 🔴 RUN VB — THE THREE BLOCKS THAT STOOD HERE, AND WHY THE GAP IS THE POINT ═════════
          Between the prose and the action row there were three things: the `stripped` list ("N
          authorities named in this answer could not be found in our corpus"), the `unchecked`
          *See also* group of cards, and the `HowWeKnow` ledger. Adam, on production, 2026-08-25:
          *"No content should be under the answer."* All three are gone. Nothing replaces them, and
          nothing is conditional — there is no envelope and no count that puts words back here.

          🔴 TWO BLOCKS WENT, NOT THE ONE THE BRIEF NAMED, AND THAT IS DECLARED. The brief removes
          "the See-also block" — `unchecked`. Its own verification gate then demands **zero**
          rendered-innerText hits on *"could not be found"*, which is the `stripped` block's
          heading, one element above. Measured on RQ04 before this run: that phrase appeared TWICE
          on screen (the `stripped` head, and the ledger's second clause). Deleting the named block
          alone would have satisfied the sentence and failed the gate, and would have left a lawyer
          reading a corpus-vocabulary list under an answer Adam had just cleared. Both go.

          ⭐ THE SIGNAL IS NOT THE EXPLANATION, AND ONLY THE EXPLANATION WAS REMOVED. Every
          authority that used to be listed here is STILL MARKED WHERE IT WAS SAID — `Prose` renders
          `<mark className="cc-v2-unresolved">` and `<mark className="cc-v2-unchecked">` with their
          titles and their screen-reader text, untouched by this run. So the reader can still tell
          *which sentence* rests on something we could not check, which is the one thing a list at
          the foot of the answer was never able to say. RUN VC rebuilds the See-also cards IN THE
          RAIL, off the same `stripped` / `unchecked` / `citeSource` fields, which is why this run
          deletes no data and changes no envelope.

          🔴 `answer.badge` IS STILL READ — below, inside `Show Authorities`, for `beyondCap`. That
          is not a survival of the ledger: it is a sentence printed only to a reader who opened the
          list, about the list they opened. The ledger's defect was that nobody asked for it. */}

      {/* ═══ 🔴 RUN CK — THE ROW WAITS FOR THE REVEAL, NOT FOR THE NETWORK. ═══════════════════════
          Adam: *"The way it is now following as the answer unfolds looks bad."* — Copy Answer /
          Open in Research / Show Authorities were rendering the instant this component MOUNTED,
          which (per the reveal's own header above) is already the COMPLETE, gate-passed answer:
          there is no network stream left to wait on. What was pushing the row down the page is
          `useReveal`'s own paced typewriter animation — `reveal.revealing` — playing out AFTER
          mount. So the row is gated on that, the real "is this still unfolding on screen" signal,
          not on a network state this component never carries.

          🔴 EVERY ANSWER ALREADY GETS ITS OWN ROW, BY CONSTRUCTION, WITHOUT ANY CHANGE HERE.
          `InteropRow` lives INSIDE this component, and `thread/Turn.jsx` mounts one `ClarkAnswerV2`
          per answered turn (`turns.map` → one `<ClarkAnswerV2>` each), each with its own `showing`
          state and now its own `reveal`. A second question does not touch the first turn's
          instance. A restored turn (`answer.restored`, set by `chatsApi.js` on reload) makes
          `useReveal`'s `instant` true, so `revealing` is `false` from the first render and the row
          is present immediately — no replay of an animation for words the reader already read.

          ⚠️ THE HOMEPAGE WIDGET SHARES THIS FILE (`src/clark-widget/index.jsx` mounts
          `ClarkAnswerV2` directly) AND ITS §4.3 AUTO-OPEN EFFECT IS A KNOWN CASUALTY, REPORTED
          RATHER THAN FIXED HERE: it clicks `[data-testid="clark-action-authorities"]` 50ms after
          `phase==='answer'`, once, with no retry. That button is now inside this gate and does not
          exist at 50ms for a real (non-reduced-motion) reveal, so the auto-open silently no-ops.
          The widget file is out of this run's scope; RUN WW or its successor owns the retry/removal
          decision. Every OTHER widget behaviour — Copy, the signed-out Open in Research intercept,
          Show Authorities itself — is untouched; only the button's mount time moved. */}
      {!reveal.revealing && (
        <InteropRow
          answer={answer}
          turnId={turnId}
          onOpenResearch={onOpenResearch}
          showing={showing}
          onToggleAuthorities={() => setShowing((v) => !v)}
        />
      )}

      {/* 🔴 RUN RM — THE PHONE'S AUTHORITIES, IN THE THREAD. Rendered on every answer and shown by
          CSS only below the phone breakpoint, where the stacked rail is hidden and this stands in
          its place. Gated on the SAME predicate the action row uses — `hasOpenableAuthorities`,
          which RUN CH2 established is the only gate there is (no feature flag) — so an answer with
          nothing to show renders no control and makes no promise. */}
      {/* 🔴🔴 RUN CX — THE GATE IS WIDENED HERE, AT THE CALL SITE, AND NOT IN
          `hasOpenableAuthorities`. That predicate answers "is there an authority list worth
          OPENING" — `badge.resolved > 0`, else `authorities.length > 0` — and it also gates the
          action row's *Open in Research* link, which resolves to `/research/answer/<id>`. A
          see-also-only answer has nothing to open in Research: those rows are precisely the ones
          the corpus does not hold, and widening the shared predicate would put a live link on an
          answer with no stored authority behind it. So the SHARED gate is untouched and the mobile
          gate gains one term.
          MEASURED, probe R-1 ("supplemental interrogatories … NJ"): 0 relied, 3 see also. Under the
          old gate that answer rendered NO mobile control at all while showing three cards at 1440. */}
      {!reveal.revealing && (hasOpenableAuthorities(answer) || (answer.stripped?.length || 0) + (answer.unchecked?.length || 0) > 0) && (
        <MobileAuthorities
          authorities={answer.authorities || []}
          /* The rail builds this list in `railPayload.js` (`stripped` + `unchecked`, each row
             carrying its own `group` and `reason`). The phone reads the SAME two envelope arrays so
             the two widths cannot disagree about which rows are see-also. */
          seeAlso={mobileSeeAlso}
          onOpenResearch={onOpenResearch}
        />
      )}

      {showing && (
        <div className="cc-v2-authorities" id="cc-v2-authorities" data-testid="clark-v2-authority-list">
          {/* PART 4.6 — "with its user message at top". The question this list belongs to is
              printed above it, because the column can hold a different answer's list a moment
              later and an unlabelled list of cases is not attributable to anything. */}
          <p className="cc-v2-authorities-q" data-testid="clark-v2-authorities-question">{answer.q}</p>
          {answer.authorities?.length ? (
            <ol className="cc-v2-auth-list">
              {answer.authorities.map((a) => <AuthorityRow key={a.id} a={a} onAskAbout={onAskAbout} />)}
            </ol>
          ) : (
            <p className="cc-ans-overblock-sub">This answer cited no authority we could resolve.</p>
          )}
          {answer.badge?.beyondCap > 0 && (
            <p className="cc-ans-overblock-sub">
              {`${answer.badge.beyondCap} further ${answer.badge.beyondCap === 1 ? "authority was" : "authorities were"} resolved and checked but are not shown — the list is capped at ${answer.badge.shown}.`}
            </p>
          )}
        </div>
      )}
      {onFeedback ? null : null}
    </div>
  );
}

/**
 * ═══ HANDOFF 6.2 — THE TWO HOMELESS PROVENANCE NOTICES ═══════════════════════════════════════
 *
 * Research's scope strip was removed and took two things with it:
 *   1. "(from your search)" — where the jurisdiction VALUE came from. The floating pill's chip can
 *      show the value but has nowhere to say its source.
 *   2. the picker-conflict warning — A5 item 7 specifies a non-blocking inline notice.
 *
 * They are re-homed here, in the answer's provenance line, because that is the one place that knows
 * both facts at once: `jurisdiction.source` says where the value came from, and `jurisdiction.notice`
 * is populated by resolveJurisdiction ONLY when the prompt and the picker actually disagreed.
 *
 * 🔴 IT NEVER ASKS, AND IT NEVER BLOCKS. The answer has already run; this states what it ran under.
 */
export function ProvenanceLine({ jurisdiction }) {
  if (!jurisdiction) return null;
  const { effective, notice, source } = jurisdiction;
  if (!notice && !effective) return null;
  return (
    <p className="cc-ans-persuasive-notice cc-v2-provenance"
       data-testid="clark-v2-provenance" role="note">
      {notice || (
        <>
          {`Answered for ${effective}`}
          {/* The half that was lost with the scope strip. "from your search" is the exact phrasing
              that went missing, kept rather than reworded. */}
          {source === "prompt" ? " (from your search)" : source === "picker" ? " (from your jurisdiction picker)" : ""}
        </>
      )}
    </p>
  );
}
